import { useRef, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface CameraControllerProps {
  target: [number, number, number] | null;
  isZoomed: boolean;
  idleLookX?: number;
}

export default function CameraController({ target, isZoomed, idleLookX = 0 }: CameraControllerProps) {
  const { camera } = useThree();
  const targetPos = useRef(new THREE.Vector3(idleLookX, 0.5, 8));
  const lookTarget = useRef(new THREE.Vector3(idleLookX, 0, 0));
  const currentPos = useRef(new THREE.Vector3());
  const currentLook = useRef(new THREE.Vector3(idleLookX, 0, 0));
  const seeded = useRef(false);

  // Seed from the camera's real position so takeover from OrbitControls doesn't snap
  useEffect(() => {
    if (!seeded.current) {
      currentPos.current.copy(camera.position);
      seeded.current = true;
    }
  }, [camera]);

  useEffect(() => {
    currentPos.current.copy(camera.position);
    if (isZoomed && target) {
      const dir = new THREE.Vector3(...target).normalize();
      targetPos.current.copy(dir.multiplyScalar(4.5));
      lookTarget.current.set(...target);
    } else {
      targetPos.current.set(idleLookX, 0.5, 8);
      lookTarget.current.set(idleLookX, 0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, isZoomed, idleLookX]);

  useFrame((_, delta) => {
    const speed = 2.5;
    currentPos.current.lerp(targetPos.current, delta * speed);
    currentLook.current.lerp(lookTarget.current, delta * speed);

    camera.position.copy(currentPos.current);
    camera.lookAt(currentLook.current);
  });

  return null;
}
